import {
  NEGOTIATION_STATUSES,
  OFFER_STATUS,
  PRODUCT_TYPE,
  PendingActionBucket,
  PendingActionCode,
  PriceUnit,
  Side,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import { ApiTags, AuditTagIds, CommonTagIds, MarketDepthTagIds } from './tags';

export type NegotiationCreateDto = {
  offerId: number;
  bidId: number;
  fixedPrice: number;
  offeredCapacity: number;
  tenorStart: string;
  tenorMonths: number;
  buyerPostCodCollateral?: number;
  sellerPostCodCollateral?: number;
};

export type NegotiationListResponseDtoItem = {
  id: number;
  offerId: number;
  bidId: number;
  projectId: string;
  projectName: string;
  buyerCompanyId: string;
  buyerCompanyName: string;
  sellerCompanyId: string;
  sellerCompanyName: string;
  reportingCounterparty: Side;
  productTypeId: PRODUCT_TYPE;
  productTypeName: string;
  fixedPrice: number;
  /** The unit of `fixedPrice`: the negotiation's offer's unit, resolved by the backend. */
  priceUnit: PriceUnit;
  offeredCapacity: number;
  tenorStart: string;
  tenorMonths: number;
  sellerPostCodCollateral: number | null;
  buyerPostCodCollateral: number | null;
  proposalSenderId: Side;
  proposalSenderName: string;
  proposalCount: number;
  negotiationStatusId: NEGOTIATION_STATUSES;
  negotiationStatusName: string;
  /**
   * The linked offer's status. Tells apart the two phases of a STAGED negotiation: while the offer
   * is AWAITING_KEY_METRICS the seller can only wait, and once it is ACTIVE the proposal is
   * sendable.
   */
  offerStatusId: OFFER_STATUS;
  offerStatusName: string;
  originatorId: string;
  originatorName: string;
  upi: string;
  buyerConfirmedAt: string | null;
  sellerConfirmedAt: string | null;
  buyerExecutedAt: string | null;
  sellerExecutedAt: string | null;
  executedDateTime: string | null;
  version: number;
  created: string;
  updated: string;
  feePayingPartyId: string;
  feePayingPartyName: string;
  isBlockTrade: boolean;
  buyerLeiPresent: boolean;
  /**
   * Resolved from `contractLegalEntity`, not from the company that listed the offer — those
   * differ whenever the seller contracts through an SPV.
   */
  sellerLeiPresent: boolean;
  /** The sell-side company actually contracting, which `sellerLeiPresent` describes. */
  contractLegalEntity: string | null;
  leiGateApplies: boolean;
  pendingAction: {
    bucket: PendingActionBucket;
    actionCode: PendingActionCode;
    documentCount: number;
  } | null;
};

export type NegotiationListResponseDto = NegotiationListResponseDtoItem[];

export type GetNegotiationsParams = {
  companyId: string;
  sideView: Side;
  status?: NEGOTIATION_STATUSES[];
};

export type CreateNegotiationParams = {
  data: NegotiationCreateDto;
  companyId: string;
  sideView: Side;
};

export type GetNegotiationParams = {
  id: number;
  companyId: string;
  sideView: Side;
};
export interface GetProposalResponseDto {
  id: number;
  negotiationId: number;
  fixedPrice: number;
  offeredCapacity: number;
  tenorStart: string;
  tenorMonths: number;
  sellerPostCodCollateral: number;
  buyerPostCodCollateral: number;
  buyerCompanyId: string;
  sellerCompanyId: string;
  buyerCompanyName: string;
  sellerCompanyName: string;
  proposalSender: Side;
  created: string;
  createdBy: string;
}

export interface GetProposalRequestParams {
  negotiationId: number;
  companyId: string;
  sideView: Side;
  negotiationProposalCount: number;
}

export type NegotiationActionDto = {
  version: number;
};

export type ConfirmNegotiationActionDto = NegotiationActionDto & {
  leiOverride?: boolean;
};

// Carries no company identity: the write ack is not a masking surface, so the backend stopped
// returning buyerCompanyId/sellerCompanyId here. Read the negotiation if you need the parties.
export type NegotiationBasicInfoResponseDto = {
  id: number;
  projectId: string;
  version: number;
  offerId: number;
  bidId: number;
  negotiationStatusId: string;
  negotiationStatusName: string;
};

export type NegotiationActionParams = {
  id: number;
  data: NegotiationActionDto;
  companyId: string;
  sideView: Side;
};

export type ConfirmNegotiationActionParams = Omit<
  NegotiationActionParams,
  'data'
> & {
  data: ConfirmNegotiationActionDto;
};

export type CounterProposalRequestDto = {
  fixedPrice: number;
  offeredCapacity: number;
  tenorStartDate: string;
  tenorMonths: number;
  version: number;
  sellerPostCodCollateral?: number;
  buyerPostCodCollateral?: number;
};

export type CounterProposalParams = {
  id: number;
  data: CounterProposalRequestDto;
  companyId: string;
  sideView: Side;
};

export type ManualExecutionDto = {
  fixedPrice: number;
  documentName?: string;
  tenorStartDate: string;
  tenorMonths: number;
  reportingCounterparty: string;
  settlementPoint: string;
  settlementPointIndex: string;
  buyersFraction: number;
  negativePricingMethod?: string;
  recProgram?: string;
  recTrackingSystem?: string;
  recDeliveryMethodology?: string;
  performanceGuaranteeMethod?: string;
  buyerPostCodCredit?: string;
  sellerPostCodCredit?: string;
  version: number;
};

export type ManualExecuteParams = {
  id: number;
  data: ManualExecutionDto;
  companyId: string;
  sideView: Side;
};

export type ExecutionTermsDto = {
  fixedPrice: number;
  /** The unit of `fixedPrice`: the negotiation's offer's unit, resolved by the backend. */
  priceUnit: PriceUnit;
  documentName: string | null;
  tenorStart: string;
  tenorMonths: number;
  reportingCounterparty: string;
  settlementPoint: string;
  settlementPointIndexId: string;
  settlementPointIndexName: string;
  buyerFraction: number;
  nameplateCapacity: number;
  offeredCapacity: number;
  notionalRounded: number;
  notionalExact: number;
  totalGeneration: number;
  avgCapacityFactor: number;
  negativePricingMethod: string | null;
  recProgram: string | null;
  recTrackingSystem: string | null;
  recDeliveryMethodology: string | null;
  performanceGuaranteeMethodId: string | null;
  buyerPostCodCollateral: string | null;
  sellerPostCodCollateral: string | null;
  isManualExecution: boolean;
};

export type GetExecutionTermsParams = {
  id: number;
  companyId: string;
  /**
   * Not sent. Part of the cache key only, so a status change means a fresh fetch: the terms change
   * when a negotiation leaves PENDING_EXECUTION_METRICS, and they are not polled (RME-1488).
   */
  status?: NEGOTIATION_STATUSES;
};

const BASE_URL = '/api/v1/negotiations';

export const negotiationsApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getNegotiations: build.query<
      NegotiationListResponseDto,
      GetNegotiationsParams
    >({
      query: ({ companyId, sideView, status }) => {
        const searchParams = new URLSearchParams();

        if (status) {
          status.forEach(s => searchParams.append('status', s));
        }

        const queryString = searchParams.toString();
        const suffix = queryString ? `?${queryString}` : '';
        const url = `${BASE_URL}${suffix}`;

        return {
          url,
          headers: {
            'X-Company-Id': companyId,
            'X-Side-View': sideView,
          },
        };
      },
      providesTags: (result, error, { companyId, sideView, status = [] }) => [
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}-${status.join('-')}`,
        },
      ],
    }),
    getNegotiation: build.query<
      NegotiationListResponseDtoItem,
      GetNegotiationParams
    >({
      query: ({ id, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}`,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      providesTags: (result, error, { id }) => [
        { type: ApiTags.NEGOTIATIONS, id },
      ],
    }),
    createNegotiation: build.mutation<
      NegotiationListResponseDtoItem,
      CreateNegotiationParams
    >({
      query: ({ data, companyId, sideView }) => ({
        url: BASE_URL,
        method: 'POST',
        body: data,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: (result, error, { companyId, sideView, data }) => [
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: `${MarketDepthTagIds.MATCHING_BIDS}-${data.offerId}`,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: `${MarketDepthTagIds.MATCHING_OFFERS}-${data.bidId}`,
        },
        ...(result
          ? [
              {
                type: ApiTags.AUDIT as const,
                id: `${AuditTagIds.NEGOTIATION}-${result.id}`,
              },
            ]
          : []),
      ],
    }),
    getProposals: build.query<
      GetProposalResponseDto[],
      GetProposalRequestParams
    >({
      query: ({ negotiationId, companyId, sideView }) => ({
        url: `${BASE_URL}/${negotiationId}/proposals`,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      providesTags: (result, error, { negotiationId, companyId }) => [
        {
          type: ApiTags.PROPOSALS,
          id: `${CommonTagIds.LIST}-${negotiationId}-${companyId}`,
        },
      ],
    }),
    confirmNegotiation: build.mutation<
      NegotiationBasicInfoResponseDto,
      ConfirmNegotiationActionParams
    >({
      query: ({ id, data, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}/actions/confirm`,
        method: 'POST',
        body: {
          version: data.version,
          ...(data.leiOverride && { leiOverride: data.leiOverride }),
        },
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: (result, error, { id, companyId, sideView }) => [
        { type: ApiTags.NEGOTIATIONS, id },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${id}`,
        },
      ],
    }),
    counterProposal: build.mutation<
      NegotiationBasicInfoResponseDto,
      CounterProposalParams
    >({
      query: ({ id, data, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}/proposals`,
        method: 'POST',
        body: data,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: (result, error, { id, companyId, sideView }) => [
        { type: ApiTags.NEGOTIATIONS, id },
        {
          type: ApiTags.PROPOSALS,
          id: `${CommonTagIds.LIST}-${id}-${companyId}`,
        },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${id}`,
        },
      ],
    }),
    acceptProposal: build.mutation<
      NegotiationBasicInfoResponseDto,
      NegotiationActionParams
    >({
      query: ({ id, data, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}/actions/accept`,
        method: 'POST',
        body: data,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: (result, error, { id, companyId, sideView }) => [
        { type: ApiTags.NEGOTIATIONS, id },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${id}`,
        },
      ],
    }),
    rejectProposal: build.mutation<
      NegotiationBasicInfoResponseDto,
      NegotiationActionParams
    >({
      query: ({ id, data, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}/actions/reject`,
        method: 'POST',
        body: data,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: (result, error, { id, companyId, sideView }) => [
        { type: ApiTags.NEGOTIATIONS, id },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${id}`,
        },
      ],
    }),
    returnToNegotiation: build.mutation<
      NegotiationBasicInfoResponseDto,
      NegotiationActionParams
    >({
      query: ({ id, data, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}/actions/return-to-negotiation`,
        method: 'POST',
        body: data,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: (result, error, { id, companyId, sideView }) => [
        { type: ApiTags.NEGOTIATIONS, id },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${id}`,
        },
      ],
    }),
    manualExecute: build.mutation<
      NegotiationBasicInfoResponseDto,
      ManualExecuteParams
    >({
      query: ({ id, data, companyId }) => ({
        url: `${BASE_URL}/${id}/actions/manual-execute`,
        method: 'POST',
        body: data,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      invalidatesTags: (result, error, { id, companyId, sideView }) => [
        { type: ApiTags.NEGOTIATIONS, id },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${id}`,
        },
      ],
    }),
    getExecutionTerms: build.query<ExecutionTermsDto, GetExecutionTermsParams>({
      query: ({ id, companyId }) => ({
        url: `${BASE_URL}/${id}/execution-terms`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
    }),
    executeNegotiation: build.mutation<
      NegotiationBasicInfoResponseDto,
      NegotiationActionParams
    >({
      query: ({ id, data, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}/actions/execute`,
        method: 'POST',
        body: data,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: (result, error, { id, companyId, sideView }) => [
        { type: ApiTags.NEGOTIATIONS, id },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${id}`,
        },
      ],
    }),
    deleteNegotiation: build.mutation<void, NegotiationActionParams>({
      query: ({ id, data, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
        body: data,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: (result, error, { id, companyId, sideView }) => [
        { type: ApiTags.NEGOTIATIONS, id },
        {
          type: ApiTags.NEGOTIATIONS,
          id: `${CommonTagIds.LIST}-${companyId}-${sideView}`,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.NEGOTIATION}-${id}`,
        },
      ],
    }),
  }),
});

export const {
  useGetNegotiationsQuery,
  useGetNegotiationQuery,
  useCreateNegotiationMutation,
  useGetProposalsQuery,
  useConfirmNegotiationMutation,
  useCounterProposalMutation,
  useAcceptProposalMutation,
  useRejectProposalMutation,
  useReturnToNegotiationMutation,
  useManualExecuteMutation,
  useGetExecutionTermsQuery,
  useExecuteNegotiationMutation,
  useDeleteNegotiationMutation,
} = negotiationsApi;
